import { Button as MuiButton } from "@mui/material";
import type {
  ElementType,
  HTMLAttributeAnchorTarget,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
} from "react";

export type ButtonVariant = "contained" | "text" | "outlined" | "textFit";
export type ButtonColor =
  | "primary"
  | "secondary"
  | "error"
  | "success"
  | "info"
  | "whatsapp";
export type ButtonSize = "small" | "medium" | "large";
export type ButtonType = "button" | "submit" | "reset";
export type ButtonBorderRoudness = "medium";

export type ButtonProps = PropsWithChildren<{
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  type?: ButtonType;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
  rel?: string;
  borderRoundness?: ButtonBorderRoudness;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  // Renders the button as a different element — use a non-interactive element
  // (e.g. "span") only inside another interactive element (e.g. ButtonBase)
  // to avoid invalid DOM nesting
  as?: ElementType;
}>;

export function Button({
  children,
  variant,
  color,
  size,
  fullWidth,
  disabled,
  loading,
  type,
  startIcon,
  endIcon,
  href,
  borderRoundness,
  onClick,
  as,
  ...rest
}: ButtonProps) {
  const isTextFitVariant = variant === "textFit";

  return (
    <MuiButton
      variant={isTextFitVariant ? "text" : variant}
      color={color}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      loading={loading}
      disableElevation
      type={type}
      startIcon={startIcon}
      endIcon={endIcon}
      href={href}
      onClick={onClick}
      disableRipple={isTextFitVariant}
      sx={{
        ...(borderRoundness === "medium" && {
          borderRadius: "var(--radius-2xl)",
        }),

        ...(isTextFitVariant && {
          padding: 0,
          width: "fit-content",
          minWidth: "unset",
          minHeight: "unset",

          "&:focus:not([disabled])": {
            backgroundColor: "transparent",
          },
          "&:hover": {
            backgroundColor: "transparent",
          },
        }),
      }}
      {...(as ? { component: as, role: undefined } : {})}
      {...rest}
    >
      {children}
    </MuiButton>
  );
}
